import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import {
    Loader2, Megaphone, Users, Clock, MessageSquare,
    AlertTriangle, Send, Save, CalendarClock,
} from 'lucide-react';
import { campaignsAPI, organizationsAPI } from '../../lib/api';

/**
 * P_USER_WELCOME_DISPATCH_3.0a + 3.0b — Campaign Builder modal.
 *
 * Draft workflow (3.0b):
 *   • Save Draft  → POST /campaigns (or PATCH existing draft).
 *   • Schedule    → date+time picker → POST /campaigns/{id}/schedule.
 *   • Send Now    → create + POST /campaigns/{id}/send-now (confirmation).
 *
 * Edit mode: clicking a draft row reopens this modal with the persisted
 * state. Subsequent saves go via PATCH instead of POST. Scheduled /
 * sending / completed / cancelled campaigns NEVER reopen this modal —
 * they open the read-only Drawer instead.
 */

const TYPES = [
    'announcement', 'training', 'recognition', 'birthday', 'emergency', 'custom',
];

const PRIORITIES = ['low', 'normal', 'high', 'urgent'];

const ROLES = ['client_admin', 'location_manager', 'employee'];

const SECTIONS = [
    { id: 'general',   icon: Megaphone },
    { id: 'audience',  icon: Users },
    { id: 'schedule',  icon: Clock },
    { id: 'message',   icon: MessageSquare },
];

const initialState = {
    title: '',
    description: '',
    type: 'announcement',
    priority: 'normal',
    require_ack: false,
    body_en: '',
    target_organization_ids: [],
    target_roles: [],
    scheduled_for_date: '',
    scheduled_for_time: '',
};

function buildFutureIso(dateStr, timeStr) {
    if (!dateStr || !timeStr) return null;
    // Treat input as the operator's local time, convert to UTC ISO.
    const local = new Date(`${dateStr}T${timeStr}`);
    if (Number.isNaN(local.getTime())) return null;
    if (local.getTime() <= Date.now()) return null;
    return local.toISOString();
}

export const CampaignBuilder = ({ open, onClose, onCreated, editingCampaign = null }) => {
    const { t } = useTranslation();
    const [section, setSection] = useState('general');
    const [form, setForm] = useState(initialState);
    const [organizations, setOrganizations] = useState([]);
    const [preview, setPreview] = useState({ total: 0, by_role: {}, by_organization: [], organizations_count: 0 });
    const [previewLoading, setPreviewLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [error, setError] = useState(null);

    const isEditing = !!editingCampaign;

    // Load orgs + hydrate form on open.
    useEffect(() => {
        if (!open) return;
        setSection('general');
        setError(null);
        if (isEditing) {
            const c = editingCampaign;
            setForm({
                title: c.title || '',
                description: c.description || '',
                type: c.type || 'announcement',
                priority: c.priority || 'normal',
                require_ack: !!c.require_ack,
                body_en: c.body_en || '',
                target_organization_ids: c.target_scope?.organization_ids || [],
                target_roles: c.target_scope?.roles || [],
                scheduled_for_date: '',
                scheduled_for_time: '',
            });
        } else {
            setForm(initialState);
        }
        organizationsAPI.list().then(r => setOrganizations(r.data?.items || r.data || []))
            .catch(() => setOrganizations([]));
    }, [open, isEditing, editingCampaign]);

    // Live audience preview.
    const targetScope = useMemo(() => ({
        organization_ids: form.target_organization_ids,
        roles: form.target_roles,
    }), [form.target_organization_ids, form.target_roles]);

    useEffect(() => {
        if (!open) return;
        if (targetScope.organization_ids.length === 0 && targetScope.roles.length === 0) {
            setPreview({ total: 0, by_role: {}, by_organization: [], organizations_count: 0 });
            return;
        }
        setPreviewLoading(true);
        let cancelled = false;
        campaignsAPI.audiencePreview(targetScope)
            .then(r => { if (!cancelled) setPreview(r.data); })
            .catch(() => { if (!cancelled) setPreview({ total: 0, by_role: {}, by_organization: [], organizations_count: 0 }); })
            .finally(() => { if (!cancelled) setPreviewLoading(false); });
        return () => { cancelled = true; };
    }, [targetScope, open]);

    const toggleOrg = (id) => setForm(f => ({
        ...f,
        target_organization_ids: f.target_organization_ids.includes(id)
            ? f.target_organization_ids.filter(x => x !== id)
            : [...f.target_organization_ids, id],
    }));
    const toggleRole = (r) => setForm(f => ({
        ...f,
        target_roles: f.target_roles.includes(r)
            ? f.target_roles.filter(x => x !== r)
            : [...f.target_roles, r],
    }));

    const canSubmit = form.title.trim().length > 0 && form.body_en.trim().length > 0;
    const canDispatch = canSubmit && preview.total > 0;
    const futureIso = buildFutureIso(form.scheduled_for_date, form.scheduled_for_time);

    // Build the payload sent to either POST or PATCH.
    const buildPayload = useCallback(() => ({
        title: form.title.trim(),
        description: form.description.trim(),
        type: form.type,
        priority: form.priority,
        require_ack: form.require_ack,
        body_en: form.body_en.trim(),
        target_scope: {
            organization_ids: form.target_organization_ids,
            roles: form.target_roles,
        },
    }), [form]);

    // Persist the current state to either create or update a draft.
    const persistDraft = useCallback(async () => {
        if (isEditing) {
            await campaignsAPI.update(editingCampaign.id, buildPayload());
            return editingCampaign.id;
        }
        const created = await campaignsAPI.create(buildPayload());
        return created.data?.id;
    }, [isEditing, editingCampaign, buildPayload]);

    const handleSaveDraft = useCallback(async () => {
        setError(null); setSubmitting(true);
        try {
            const cid = await persistDraft();
            onCreated?.(cid, { kind: 'draft' });
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'save_failed');
        } finally { setSubmitting(false); }
    }, [persistDraft, onClose, onCreated]);

    const handleSchedule = useCallback(async () => {
        setError(null);
        if (!futureIso) {
            setError(t('campaignBuilder.schedule.invalid'));
            setSection('schedule');
            return;
        }
        setSubmitting(true);
        try {
            const cid = await persistDraft();
            if (!cid) throw new Error('save_failed');
            await campaignsAPI.schedule(cid, futureIso);
            onCreated?.(cid, { kind: 'scheduled' });
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'schedule_failed');
        } finally { setSubmitting(false); }
    }, [persistDraft, futureIso, onClose, onCreated, t]);

    const handleSendNow = useCallback(async () => {
        setError(null); setSubmitting(true);
        try {
            const cid = await persistDraft();
            if (!cid) throw new Error('save_failed');
            await campaignsAPI.sendNow(cid);
            setConfirming(false);
            onCreated?.(cid, { kind: 'sent' });
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'send_failed');
        } finally { setSubmitting(false); }
    }, [persistDraft, onClose, onCreated]);

    if (!open) return null;

    return (
        <>
            <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="campaign-builder-modal">
                    <DialogHeader>
                        <DialogTitle data-testid="campaign-builder-title">
                            {isEditing ? t('campaignBuilder.editTitle') : t('campaignBuilder.title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('campaignBuilder.subtitle')}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Section tabs */}
                    <div className="flex gap-1 border-b border-border overflow-x-auto" data-testid="campaign-builder-tabs">
                        {SECTIONS.map(s => {
                            const Icon = s.icon;
                            return (
                                <button key={s.id}
                                    onClick={() => setSection(s.id)}
                                    className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap inline-flex items-center gap-2
                                        ${section === s.id ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                                    data-testid={`campaign-section-${s.id}`}>
                                    <Icon className="w-4 h-4" />
                                    {t(`campaignBuilder.sections.${s.id}`)}
                                </button>
                            );
                        })}
                    </div>

                    {/* General */}
                    {section === 'general' && (
                        <div className="space-y-4 py-2" data-testid="campaign-section-general-body">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    {t('campaignBuilder.fields.title')}
                                </label>
                                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                                    placeholder={t('campaignBuilder.fields.titlePlaceholder')}
                                    data-testid="campaign-input-title" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    {t('campaignBuilder.fields.description')}
                                </label>
                                <Textarea value={form.description}
                                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                                    placeholder={t('campaignBuilder.fields.descriptionPlaceholder')}
                                    rows={2} data-testid="campaign-input-description" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">{t('campaignBuilder.fields.type')}</label>
                                    <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}
                                        className="w-full bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground"
                                        data-testid="campaign-select-type">
                                        {TYPES.map(tp => (
                                            <option key={tp} value={tp}>{t(`campaignBuilder.type.${tp}`)}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">{t('campaignBuilder.fields.priority')}</label>
                                    <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}
                                        className="w-full bg-background border border-input rounded-md h-9 px-3 text-sm text-foreground"
                                        data-testid="campaign-select-priority">
                                        {PRIORITIES.map(p => (
                                            <option key={p} value={p}>{t(`campaignBuilder.priority.${p}`)}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <label className="flex items-center gap-2 text-sm text-foreground cursor-pointer">
                                <input type="checkbox" checked={form.require_ack}
                                    onChange={(e) => setForm({ ...form, require_ack: e.target.checked })}
                                    data-testid="campaign-input-require-ack" />
                                {t('campaignBuilder.fields.requireAck')}
                            </label>
                        </div>
                    )}

                    {/* Audience */}
                    {section === 'audience' && (
                        <div className="space-y-4 py-2" data-testid="campaign-section-audience-body">
                            <div>
                                <div className="text-sm font-medium text-foreground mb-2">{t('campaignBuilder.fields.organizations')}</div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1 max-h-48 overflow-y-auto border border-border rounded-md p-2">
                                    {organizations.length === 0 ? (
                                        <div className="text-muted-foreground text-sm px-2 py-1">{t('campaignBuilder.fields.noOrgs')}</div>
                                    ) : organizations.map(o => (
                                        <label key={o.id} className="flex items-center gap-2 text-sm text-foreground hover:bg-muted/40 rounded px-2 py-1 cursor-pointer">
                                            <input type="checkbox" checked={form.target_organization_ids.includes(o.id)}
                                                onChange={() => toggleOrg(o.id)}
                                                data-testid={`campaign-org-${o.id}`} />
                                            {o.name}
                                        </label>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-sm font-medium text-foreground mb-2">{t('campaignBuilder.fields.roles')}</div>
                                <div className="flex flex-wrap gap-2">
                                    {ROLES.map(r => (
                                        <button key={r} type="button"
                                            onClick={() => toggleRole(r)}
                                            className={`px-3 py-1 text-sm rounded-full border transition-colors
                                                ${form.target_roles.includes(r)
                                                    ? 'bg-primary text-primary-foreground border-primary'
                                                    : 'bg-background text-foreground border-input hover:bg-muted/60'}`}
                                            data-testid={`campaign-role-${r}`}>
                                            {t(`campaignBuilder.roles.${r}`)}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-1">{t('campaignBuilder.fields.rolesHint')}</p>
                            </div>
                            <Card className="p-3 bg-muted/40" data-testid="campaign-audience-preview">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-medium text-foreground">{t('campaignBuilder.preview.title')}</span>
                                    {previewLoading ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : null}
                                </div>
                                <div className="text-3xl font-semibold text-foreground" data-testid="campaign-recipient-count">
                                    {preview.total} <span className="text-sm font-normal text-muted-foreground">{t('campaignBuilder.preview.recipients')}</span>
                                </div>
                                {preview.total > 0 ? (
                                    <div className="mt-2 flex flex-wrap gap-2 text-xs text-muted-foreground">
                                        {Object.entries(preview.by_role).map(([role, n]) => (
                                            <Badge key={role} variant="outline" data-testid={`preview-role-${role}`}>
                                                {n} {t(`campaignBuilder.roles.${role}`)}
                                            </Badge>
                                        ))}
                                        <Badge variant="outline" data-testid="preview-org-count">
                                            {preview.organizations_count} {t('campaignBuilder.preview.orgs')}
                                        </Badge>
                                    </div>
                                ) : (
                                    <div className="text-xs text-muted-foreground mt-1">{t('campaignBuilder.preview.empty')}</div>
                                )}
                            </Card>
                        </div>
                    )}

                    {/* Schedule — date + time picker */}
                    {section === 'schedule' && (
                        <div className="space-y-3 py-2" data-testid="campaign-section-schedule-body">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">{t('campaignBuilder.schedule.date')}</label>
                                    <Input type="date" value={form.scheduled_for_date}
                                        onChange={(e) => setForm({ ...form, scheduled_for_date: e.target.value })}
                                        data-testid="campaign-schedule-date" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">{t('campaignBuilder.schedule.time')}</label>
                                    <Input type="time" value={form.scheduled_for_time}
                                        onChange={(e) => setForm({ ...form, scheduled_for_time: e.target.value })}
                                        data-testid="campaign-schedule-time" />
                                </div>
                            </div>
                            <p className="text-xs text-muted-foreground">{t('campaignBuilder.schedule.localNotice')}</p>
                        </div>
                    )}

                    {/* Message */}
                    {section === 'message' && (
                        <div className="space-y-3 py-2" data-testid="campaign-section-message-body">
                            <label className="block text-sm font-medium text-foreground">{t('campaignBuilder.message.masterEn')}</label>
                            <Textarea value={form.body_en}
                                onChange={(e) => setForm({ ...form, body_en: e.target.value })}
                                placeholder={t('campaignBuilder.message.placeholder')}
                                rows={8} data-testid="campaign-input-body-en" />
                            <div className="flex items-start gap-2 text-xs text-muted-foreground">
                                <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>{t('campaignBuilder.message.translationNotice')}</span>
                            </div>
                        </div>
                    )}

                    {error ? (
                        <div className="text-sm text-rose-600 dark:text-rose-400 flex items-start gap-2" data-testid="campaign-builder-error">
                            <AlertTriangle className="w-4 h-4 mt-0.5" />
                            <span>{String(error)}</span>
                        </div>
                    ) : null}

                    <DialogFooter className="gap-2 flex-wrap">
                        <Button variant="outline" onClick={onClose} disabled={submitting} data-testid="campaign-builder-cancel">
                            {t('campaignBuilder.cancel')}
                        </Button>
                        <Button variant="outline" disabled={!canSubmit || submitting}
                            onClick={handleSaveDraft} data-testid="campaign-builder-save-draft">
                            <Save className="w-4 h-4 mr-2" />
                            {t('campaignBuilder.saveDraft')}
                        </Button>
                        <Button variant="outline" disabled={!canDispatch || !futureIso || submitting}
                            onClick={handleSchedule} data-testid="campaign-builder-schedule">
                            <CalendarClock className="w-4 h-4 mr-2" />
                            {t('campaignBuilder.scheduleAction')}
                        </Button>
                        <Button disabled={!canDispatch || submitting}
                            onClick={() => setConfirming(true)} data-testid="campaign-builder-send">
                            <Send className="w-4 h-4 mr-2" />
                            {t('campaignBuilder.sendNow')}
                            {preview.total > 0 ? <span className="ml-2 opacity-80 text-xs">({preview.total})</span> : null}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Confirm dialog */}
            <Dialog open={confirming} onOpenChange={(v) => !submitting && setConfirming(v)}>
                <DialogContent className="max-w-md" data-testid="campaign-confirm-modal">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            {t('campaignBuilder.confirm.title')}
                        </DialogTitle>
                        <DialogDescription>
                            {t('campaignBuilder.confirm.body', { count: preview.total })}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" disabled={submitting} onClick={() => setConfirming(false)} data-testid="campaign-confirm-cancel">
                            {t('campaignBuilder.cancel')}
                        </Button>
                        <Button disabled={submitting} onClick={handleSendNow} data-testid="campaign-confirm-send">
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                            {t('campaignBuilder.confirm.button')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default CampaignBuilder;
